import React, { ReactNode } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  makeStyles,
  Typography,
  ButtonBase,
} from '@material-ui/core'
import { Theme } from '@material-ui/core/styles'
import {
  FormattedMessage,
  FormattedNumber,
  IntlShape,
  useIntl,
} from 'react-intl'
import CloseIcon from '@material-ui/icons/Close'
import EditIcon from '@src/icons/EditIcon'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import useFormikHelper from '../useFormikHelper'
import InfoCircleIcon from '@src/icons/InfoCircleIcon'
import { truncateLongString, getDisplayName } from '../utils'
import HoverToolTip from '@src/components/HoverToolTip/HoverTooltipForForms'
import {
  SingleTubeFormValues,
  useProductFromQueryParam,
} from '../ProductsConfiguration'
import { isNil } from 'lodash'
import { BiTubeProduct } from '../../BiTube/enum'
import { messages } from '@src/routes/Configurators/messages'
import { formatOverhangSequenceRedesign } from '../../../utils/formatOverhangSequence'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
  noBottomBorder: {
    borderBottom: 'unset',
  },
  nestedBox: {
    margin: theme.spacing(1),
  },
  nestedTable: {
    backgroundColor: '#f5f5f5',
    marginBottom: '16px',
    margin: '0 auto',
    width: '72%',
    '& th, & td': {
      border: 'none',
    },
  },
  closeIconStyle: {
    fontSize: theme.typography.pxToRem(12),
    cursor: 'pointer',
    fontWeight: 'bold',
    stroke: 'currentColor',
    strokeWidth: 2,
    marginLeft: theme.spacing(4),
  },
  editIconStyle: {
    fontSize: theme.typography.pxToRem(16),
    cursor: 'pointer',
  },
  linkStyle: {
    fontWeight: 'normal',
    cursor: 'pointer',
  },
  infoIconStyle: {
    fontSize: theme.typography.pxToRem(16),
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.error.main,
    display: 'inline',
    marginRight: theme.spacing(1.75),
  },
  warningIconStyle: {
    fontSize: theme.typography.pxToRem(18),
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.warning.main,
    display: 'inline',
    marginRight: theme.spacing(1.75),
  },
  ghostIcon: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    display: 'inline-block',
    marginRight: theme.spacing(2.75),
  },
  nameStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    width: theme.spacing(22),
    gap: theme.spacing(1.25),
  },
}))

interface OligoData {
  oligoName: string
  price: any
  sequenceType: string
  sequence: any
  amount: string | undefined
  tubes: string | undefined
  purification?: string | ReactNode
  format?: string | ReactNode
  bases: number | string
}

const defaultValue = '-'
const renderValue = (v?: string | number) => {
  return v || defaultValue
}
const renderSequence = (
  oligo: SingleTubeFormValues,
  renderClass: string,
  antisense = false
) => {
  const buildSequence = (...parts: (string | undefined)[]) =>
    parts.filter((p) => p && p !== '[None]').join('')

  const rawFullSequence = buildSequence(
    oligo.fiveMod,
    oligo.sequence,
    oligo.customerOverhang,
    oligo.threeMod
  )
  const fullSequence = formatOverhangSequenceRedesign(rawFullSequence)

  const rawFullAntisense = buildSequence(
    oligo.antisenceFiveMod,
    oligo.antisenceSequence,
    oligo.antisenceThreeMod,
    oligo.customerAntisenseOverHang
  )
  const fullAntisenseSequence = formatOverhangSequenceRedesign(rawFullAntisense)

  const sequence = antisense ? fullAntisenseSequence : fullSequence
  const displaySequence = truncateLongString(sequence, 18)
  const isLong = renderValue(sequence).toString().length > 18

  const SequenceContent = (
    <Typography color="primary" variant="caption" className={renderClass}>
      {renderValue(displaySequence)}
    </Typography>
  )

  return isLong ? (
    <HoverToolTip toolTipText={renderValue(sequence)} delay={1}>
      {SequenceContent}
    </HoverToolTip>
  ) : (
    SequenceContent
  )
}

const renderPrice = (
  price: string | undefined,
  currency: string | undefined
) => {
  if (!isNil(price)) {
    return price.toLowerCase().includes('quote') ? (
      <FormattedMessage id="QUOTE_ITEM" defaultMessage="Quote Item" />
    ) : !isNaN(Number(price)) ? (
      <FormattedNumber
        style="currency"
        currencyDisplay="symbol"
        currency={currency}
        value={Number(price)}
      />
    ) : (
      <span>-</span>
    )
  }
  return <span>-</span>
}

const generateOligoPairs = (
  inputArray: SingleTubeFormValues[],
  sequenceClass: string,
  product: string,
  intl: IntlShape
): OligoData[] => {
  const result: OligoData[] = []

  const renderTranslation = (item?: string) => {
    return messages[getDisplayName(item)]
      ? intl.formatMessage(messages[getDisplayName(item)])
      : renderValue(getDisplayName(item))
  }

  const renderPurificationColumn =
    product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna
  const renderFormatColumn = product === BiTubeProduct.Duplex
  inputArray.forEach((input) => {
    const hasSenseSequenceError = input.listErrors?.some(
      (err) => err.key.toLowerCase() === 'sequence'
    )

    const hasAntisenseSequenceError = input.listErrors?.some(
      (err) => err.key.toLowerCase() === 'antisencesequence'
    )

    result.push({
      oligoName: input.name,
      price: renderPrice(input.price, input.currency),
      sequenceType:
        product === BiTubeProduct.LightCycler ? 'Donor Oligo' : 'Sense',
      sequence:
        (product === BiTubeProduct.Duplex ||
          product === BiTubeProduct.FastDuplex ||
          product === BiTubeProduct.mirna) &&
        hasSenseSequenceError
          ? '--'
          : renderSequence(input, sequenceClass),
      amount: input.amount,
      bases:
        (product === BiTubeProduct.Duplex ||
          product === BiTubeProduct.FastDuplex ||
          product === BiTubeProduct.mirna) &&
        hasSenseSequenceError
          ? '--'
          : input.baseCount
            ? Number(input.baseCount)
            : 0,
      ...(renderPurificationColumn && {
        purification: renderTranslation(input.purification),
      }),
      ...(renderFormatColumn && { format: renderTranslation(input.format) }),
      tubes: input.tubes || '1',
    })

    result.push({
      oligoName: input.antisenceName || `${input.name}_antisense`,
      price: renderPrice(input.price, input.currency),
      sequenceType:
        product === BiTubeProduct.LightCycler ? 'Acceptor Oligo' : 'Antisense',
      sequence:
        (product === BiTubeProduct.Duplex ||
          product === BiTubeProduct.FastDuplex ||
          product === BiTubeProduct.mirna) &&
        hasAntisenseSequenceError
          ? '--'
          : renderSequence(input, sequenceClass, true),
      amount: input.amount,
      bases:
        (product === BiTubeProduct.Duplex ||
          product === BiTubeProduct.FastDuplex ||
          product === BiTubeProduct.mirna) &&
        hasAntisenseSequenceError
          ? '--'
          : input.antisenseBaseCount
            ? Number(input.antisenseBaseCount)
            : 0,
      ...(renderPurificationColumn && {
        purification: renderTranslation(input.purification),
      }),
      ...(renderFormatColumn && { format: renderTranslation(input.format) }),
      tubes: input.tubes || '1',
    })
  })

  return result
}

const groupDataByPairs = (data: OligoData[], product: string) => {
  const result: any[] = []
  const renderPurificationColumn =
    product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna

  for (let i = 0; i < data.length; i += 2) {
    if (i + 1 < data.length) {
      const senseRow = data[i]
      const antisenseRow = data[i + 1]

      const [first, second] =
        product === BiTubeProduct.mirna
          ? [antisenseRow, senseRow]
          : [senseRow, antisenseRow]

      result.push({
        oligoName: senseRow.oligoName,
        price: senseRow.price,
        details: [
          {
            sequenceType: first.sequenceType,
            sequence: first.sequence,
            amount: first.amount,
            ...(renderPurificationColumn && {
              purification: first.purification,
            }),
            tubes: first.tubes,
            format: first.format,
            bases: first.bases,
          },
          {
            sequenceType: second.sequenceType,
            sequence: second.sequence,
            amount: second.amount,
            tubes: second.tubes,
            ...(renderPurificationColumn && {
              purification: second.purification,
            }),
            format: second.format,
            bases: second.bases,
          },
        ],
      })
    }
  }

  return result
}

const Row = (props: {
  oligoName: string
  price: string | undefined
  details: any[]
  handleCloseIconClick: (id: number) => null
  handleEditIconClick: (id: number) => null
  oligoId: number
  currency: string | undefined
}) => {
  const {
    oligoName,
    price,
    details,
    handleCloseIconClick,
    oligoId,
    handleEditIconClick,
  } = props
  const classes = useStyles()
  const formik = useFormikHelper()
  const product = useProductFromQueryParam()
  const renderPurificationColumn =
    product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna
  const renderFormatColumn = product === BiTubeProduct.Duplex
  const renderTubesColumn = product !== BiTubeProduct.FastDuplex
  const hasError = !!formik.allValues.oligoList[oligoId]?.listErrors?.length
  const hasWarning = !!formik.allValues.oligoList[oligoId]?.listWarning?.length
  const noOligoError = !formik.allValues.oligoList?.find(
    (val) => val?.listErrors?.length
  )

  return (
    <React.Fragment>
      <TableRow>
        <TableCell align="left" className={classes.noBottomBorder}>
          <Box display="flex" alignItems="center">
            {hasError ? (
              <InfoCircleIcon className={classes.infoIconStyle} />
            ) : hasWarning && noOligoError ? (
              <WarningRoundedIcon className={classes.warningIconStyle} />
            ) : (
              <Box className={classes.ghostIcon} />
            )}
            <ButtonBase
              onClick={() => handleEditIconClick(oligoId)}
              className={classes.nameStyle}
            >
              <Typography
                color="primary"
                variant="caption"
                className={classes.linkStyle}
              >
                {renderValue(truncateLongString(oligoName, 8))}
              </Typography>
              <EditIcon
                onClick={() => handleEditIconClick(oligoId)}
                className={classes.editIconStyle}
                color="primary"
              />
            </ButtonBase>
          </Box>
        </TableCell>
        <TableCell align="right" className={classes.noBottomBorder}>
          {hasError ? '_' : price}
        </TableCell>
        <TableCell
          className={classes.noBottomBorder}
          style={{ paddingInline: 0 }}
        >
          <Box display="flex" alignItems="center">
            <CloseIcon
              onClick={() => handleCloseIconClick(oligoId)}
              className={classes.closeIconStyle}
            />
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, margin: '0 auto' }}
          colSpan={3}
        >
          <Box className={classes.nestedBox}>
            <Table
              size="small"
              aria-label="nested-details"
              className={classes.nestedTable}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {product === BiTubeProduct.mirna ? (
                      <FormattedMessage
                        id="SEQUENCE_TYPE"
                        defaultMessage="Sequence Type"
                      />
                    ) : (
                      <FormattedMessage
                        id="OLIGO_SEQUENCE_TYPE"
                        defaultMessage="Oligo/Sequence Type"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="SEQUENCE" defaultMessage="Sequence" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="AMOUNT" defaultMessage="Amount" />
                  </TableCell>
                  {renderPurificationColumn && (
                    <TableCell>
                      <FormattedMessage
                        id="PURIFICATION"
                        defaultMessage="Purification"
                      />
                    </TableCell>
                  )}
                  {renderFormatColumn && (
                    <TableCell>
                      <FormattedMessage id="FORMAT" defaultMessage="Format" />
                    </TableCell>
                  )}
                  {renderTubesColumn && (
                    <TableCell>
                      <FormattedMessage id="TUBES" defaultMessage="Tubes" />
                    </TableCell>
                  )}
                  <TableCell>
                    <FormattedMessage id="BASES" defaultMessage="Bases" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.map((detailRow, index) => (
                  <TableRow key={index}>
                    <TableCell component="th" scope="row">
                      {detailRow.sequenceType}
                    </TableCell>
                    <TableCell>{detailRow.sequence}</TableCell>
                    <TableCell>{detailRow.amount}</TableCell>
                    {renderPurificationColumn && (
                      <TableCell>{detailRow.purification}</TableCell>
                    )}
                    {renderFormatColumn && (
                      <TableCell style={{ whiteSpace: 'nowrap' }}>
                        {detailRow.format}
                      </TableCell>
                    )}
                    {renderTubesColumn && (
                      <TableCell>{detailRow.tubes}</TableCell>
                    )}
                    <TableCell>{detailRow.bases}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </TableCell>
      </TableRow>
    </React.Fragment>
  )
}

const BiTubeTable = ({
  tableData,
  handleCloseIconClick,
  handleEditIconClick,
}) => {
  const classes = useStyles()
  const product = useProductFromQueryParam()
  const intl = useIntl()
  const flatData = generateOligoPairs(
    tableData,
    classes.linkStyle,
    product,
    intl
  )
  const groupedPairs = groupDataByPairs(flatData, product)
  return (
    <TableContainer className={classes.root}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow style={{ backgroundColor: '#F8F8FC' }}>
            <TableCell align="left">
              <Box className={classes.ghostIcon} />
              {product === 'duplex' ? (
                <FormattedMessage
                  id="CONF_OLIGO_NAME_duplex"
                  defaultMessage="Duplex Name"
                />
              ) : (
                <FormattedMessage
                  id="CONF_OLIGO_NAME_standard"
                  defaultMessage="Oligo Name"
                />
              )}
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="PRICE" defaultMessage="Price" />
            </TableCell>
            <TableCell
              align="right"
              style={{ paddingInline: 0, width: '40px' }}
            >
              <CloseIcon
                className={classes.closeIconStyle}
                style={{
                  opacity: '0',
                  pointerEvents: 'none',
                  paddingLeft: 8,
                  paddingRight: 0,
                }}
              />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupedPairs.map((item, index) => (
            <Row
              key={index}
              oligoName={item.oligoName}
              price={item.price}
              details={item.details}
              handleCloseIconClick={handleCloseIconClick}
              handleEditIconClick={handleEditIconClick}
              oligoId={index}
              currency={item.currency}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default BiTubeTable
